import { useMemo, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { classifyIp, detectIp, type IpKind } from '../lib/ipValidate';

const SAMPLES: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'IPv4 公网', value: '8.8.8.8' },
  { label: 'IPv4 私网', value: '192.168.1.1' },
  { label: 'IPv4 回环', value: '127.0.0.1' },
  { label: 'IPv4 链路本地', value: '169.254.10.20' },
  { label: 'IPv4 组播', value: '239.255.255.250' },
  { label: 'IPv4 CIDR', value: '10.0.0.0/8' },
  { label: 'IPv6 全球单播', value: '2606:4700:4700::1111' },
  { label: 'IPv6 ULA', value: 'fd00::1' },
  { label: 'IPv6 链路本地', value: 'fe80::1' },
  { label: 'IPv6 文档', value: '2001:db8::1' },
  { label: 'IPv6 CIDR', value: 'fc00::/7' },
];

const KIND_LABEL: Record<IpKind, string> = {
  ipv4: 'IPv4',
  ipv6: 'IPv6',
  'cidr-v4': 'IPv4 CIDR',
  'cidr-v6': 'IPv6 CIDR',
  invalid: '非法',
};

/**
 * `/tools/validate/ip-validate` — IPv4 / IPv6 / CIDR validator with
 * classification (private / loopback / link-local / multicast /
 * reserved / documentation).
 *
 * Sister of `network/ip-subnet`: that tool computes subnet maths
 * from an already-legal CIDR; this tool answers "is this string
 * a valid IP, and what kind".
 */
export default function IpValidateTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLES[0].value);
  const trimmed = input.trim();
  const kind = useMemo(() => detectIp(trimmed), [trimmed]);
  const cls = useMemo(() => classifyIp(trimmed), [trimmed]);

  const ok = kind !== 'invalid';

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="ip-validate"
        category="validate"
        description="IPv4 / IPv6 / CIDR 字符串校验 + 私网 / 回环 / 链路本地 / 组播 / 保留 / 文档段分类。与 network/ip-subnet 的边界：那个是子网计算器（输入已合法），本工具是格式校验 + 类别判断。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            示例
          </span>
        </header>
        <div className="flex flex-wrap gap-1.5">
          {SAMPLES.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => setInput(s.value)}
              className="rounded border border-line px-2 py-0.5 text-[12px] text-ink-muted transition hover:border-accent/40 hover:text-ink"
              title={s.value}
            >
              {s.label}
            </button>
          ))}
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            地址
          </span>
          <ResultBadge ok={ok} kind={kind} />
        </header>
        <input
          type="text"
          spellCheck={false}
          autoComplete="off"
          className="input font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="192.168.1.1 / fc00::/7 / 8.8.8.8/24"
        />
      </section>

      {ok && cls && (
        <section className="card-pad flex flex-col gap-1">
          <header className="text-[11px] uppercase tracking-wide text-ink-dim">
            分类
          </header>
          <div className="flex flex-wrap gap-1.5">
            <ClassChip on={cls.isLoopback} label="回环" />
            <ClassChip on={cls.isLinkLocal} label="链路本地" />
            <ClassChip on={cls.isPrivate} label="私网" />
            <ClassChip on={cls.isMulticast} label="组播" />
            <ClassChip on={cls.isDocumentation} label="文档段" />
            <ClassChip on={cls.isReserved} label="保留" />
            {!cls.isLoopback &&
              !cls.isLinkLocal &&
              !cls.isPrivate &&
              !cls.isMulticast &&
              !cls.isDocumentation &&
              !cls.isReserved && (
                <span className="badge border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                  公网 / 全球单播
                </span>
              )}
          </div>
          {cls.matchedRange && (
            <div className="text-[11px] text-ink-dim">
              匹配范围：<code className="font-mono">{cls.matchedRange}</code>
            </div>
          )}
        </section>
      )}

      {!ok && trimmed && (
        <section className="card-pad border-red-500/30 bg-red-500/5 text-[12.5px] text-red-600 dark:text-red-400">
          <XCircle className="mr-1.5 inline-block h-3.5 w-3.5 align-text-bottom" />
          {trimmed.includes('/')
            ? '不是合法的 CIDR：请确认地址正确，前缀长度在 0–32 (IPv4) 或 0–128 (IPv6) 之间'
            : '不是合法的 IPv4 或 IPv6 地址'}
        </section>
      )}
    </div>
  );
}

function ClassChip({
  on,
  label,
}: {
  on: boolean;
  label: string;
}): JSX.Element {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition',
        on
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-dim',
      )}
    >
      {label}
    </span>
  );
}

function ResultBadge({
  ok,
  kind,
}: {
  ok: boolean;
  kind: IpKind;
}): JSX.Element {
  if (!ok) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[11px] text-red-600 dark:text-red-400">
        <XCircle className="h-3 w-3" />
        非法
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="h-3 w-3" />
      {KIND_LABEL[kind]}
    </span>
  );
}
